import { SEOHead } from '@/components/seo/SEOHead';

const LAST_UPDATED = 'September 25, 2026';
const COMPANY_NAME = 'Rogue Move Media LLC';
const COMPANY_ADDRESS = '30 N Gould St #55327, Sheridan, WY 82801';
const SUPPORT_EMAIL = 'support@rooflyzer.pro';

export const PrivacyPolicyPage = () => {
  return (
    <>
      <SEOHead
        title="Privacy Policy"
        description="Rooflyzer's privacy policy. Learn how we collect, use, protect, and share your data when you use our roofing CRM platform."
        canonicalUrl="/privacy"
        robots="index, follow"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Privacy Policy', url: '/privacy' },
        ]}
      />

      <section className="pt-24 pb-24 px-4">
        <div className="max-w-3xl mx-auto prose prose-invert prose-sm">
          <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-white/50 text-sm mb-8">Last updated: {LAST_UPDATED}</p>

          <p className="text-white/80">
            {COMPANY_NAME} (&quot;Rooflyzer,&quot; &quot;we,&quot; &quot;us,&quot; or
            &quot;our&quot;) operates the{' '}
            <a href="https://rooflyzer.pro" className="text-emerald-400 hover:underline">
              rooflyzer.pro
            </a>{' '}
            website and the Rooflyzer OS platform (the &quot;Service&quot;). This Privacy Policy
            describes how we collect, use, disclose, and protect your information when you visit our
            website or use our Service.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">1. Information We Collect</h2>

          <h3 className="text-lg font-semibold text-white mt-6 mb-2">Personal Information</h3>
          <ul className="text-white/70 space-y-1">
            <li>Name and email address when you create an account</li>
            <li>Phone number (optional, for phone sign-in)</li>
            <li>Company name and business address</li>
            <li>Payment information (processed by Stripe — we do not store card numbers)</li>
            <li>Profile photo (optional)</li>
          </ul>

          <h3 className="text-lg font-semibold text-white mt-6 mb-2">Business Data</h3>
          <ul className="text-white/70 space-y-1">
            <li>Lead and customer records you enter into the platform</li>
            <li>Estimates, proposals, contracts, and invoices you create</li>
            <li>Appointment schedules and notes</li>
            <li>Territory and property data from public records</li>
            <li>Email and SMS campaign content and analytics</li>
          </ul>

          <h3 className="text-lg font-semibold text-white mt-6 mb-2">Automatically Collected</h3>
          <ul className="text-white/70 space-y-1">
            <li>IP address, browser type, and operating system</li>
            <li>Pages visited, time spent, and referral source</li>
            <li>Device identifiers and cookies</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            2. How We Use Your Information
          </h2>
          <ul className="text-white/70 space-y-1">
            <li>To provide and maintain the Service</li>
            <li>To process your transactions and send transaction-related communications</li>
            <li>To send administrative information (updates, security alerts, support messages)</li>
            <li>To personalize your experience and deliver relevant content</li>
            <li>To improve our Service through analytics and usage patterns</li>
            <li>To respond to your comments, questions, and support requests</li>
            <li>To detect, prevent, and address fraud or technical issues</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            3. Data Sharing & Disclosure
          </h2>
          <p className="text-white/80">
            We do <strong className="text-white">not</strong> sell your personal information. We may
            share data with:
          </p>
          <ul className="text-white/70 space-y-1">
            <li>
              <strong className="text-white/90">Service providers:</strong> Supabase (database),
              Cloudflare Pages (hosting), Stripe (payments), Cloudflare (CDN), Resend (email
              delivery)
            </li>
            <li>
              <strong className="text-white/90">Legal requirements:</strong> If required by law,
              subpoena, or government request
            </li>
            <li>
              <strong className="text-white/90">Business transfers:</strong> In connection with a
              merger, acquisition, or sale of assets
            </li>
            <li>
              <strong className="text-white/90">With your consent:</strong> When you explicitly
              authorize sharing
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            4. Third-Party Integration Data (including QuickBooks)
          </h2>
          <p className="text-white/80">
            When you connect third-party integrations, Rooflyzer processes integration data only to
            provide requested sync and workflow functionality.
          </p>
          <ul className="text-white/70 space-y-1">
            <li>
              <strong className="text-white/90">QuickBooks Online:</strong> We may access company
              profile data, customer records, invoices, and payment metadata to support accounting
              synchronization features you enable.
            </li>
            <li>
              <strong className="text-white/90">OAuth credentials:</strong> Integration access and
              refresh tokens are stored encrypted at rest and used only for authorized API calls.
            </li>
            <li>
              <strong className="text-white/90">Disconnect behavior:</strong> When you disconnect
              QuickBooks, Rooflyzer revokes OAuth access (when token revocation is available) and
              removes the integration record from your tenant.
            </li>
            <li>
              <strong className="text-white/90">No data sale:</strong> We do not sell QuickBooks or
              other integration data.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">5. Data Security</h2>
          <p className="text-white/80">
            We implement industry-standard security measures including:
          </p>
          <ul className="text-white/70 space-y-1">
            <li>TLS 1.3 encryption for all data in transit</li>
            <li>AES-256 encryption for data at rest</li>
            <li>Row-Level Security (RLS) for multi-tenant data isolation</li>
            <li>Regular security audits and vulnerability scanning</li>
            <li>Role-based access control with least-privilege principles</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">6. Data Retention</h2>
          <p className="text-white/80">
            We retain your personal information for as long as your account is active or as needed
            to provide the Service. Business data (leads, invoices, etc.) is retained until you
            delete it or close your account. After account closure, data is permanently deleted
            within 90 days, except where retention is required by law.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">7. Your Rights</h2>
          <p className="text-white/80">
            Depending on your jurisdiction, you may have the right to:
          </p>
          <ul className="text-white/70 space-y-1">
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Delete your data (&quot;right to be forgotten&quot;)</li>
            <li>Export your data in a portable format</li>
            <li>Object to or restrict processing</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p className="text-white/80 mt-2">
            To exercise these rights, contact us at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-emerald-400 hover:underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">8. Cookies</h2>
          <p className="text-white/80">
            We use essential cookies for authentication and session management. We use analytics
            cookies (Google Analytics) to understand how visitors use our site. You can disable
            non-essential cookies in your browser settings.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">9. Children&apos;s Privacy</h2>
          <p className="text-white/80">
            Our Service is not directed to individuals under 18. We do not knowingly collect
            personal information from children. If we learn that we have collected data from a
            child, we will delete it promptly.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">10. Changes to This Policy</h2>
          <p className="text-white/80">
            We may update this Privacy Policy from time to time. We will notify you of material
            changes by posting the new policy on this page and updating the &quot;Last updated&quot;
            date. Continued use of the Service after changes constitutes acceptance of the updated
            policy.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8 mb-3">11. Contact Us</h2>
          <p className="text-white/80">
            If you have questions about this Privacy Policy, please contact us:
          </p>
          <ul className="text-white/70 space-y-1">
            <li>
              <strong className="text-white/90">Email:</strong>{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-emerald-400 hover:underline">
                {SUPPORT_EMAIL}
              </a>
            </li>
            <li>
              <strong className="text-white/90">Address:</strong> {COMPANY_NAME}, {COMPANY_ADDRESS}
            </li>
          </ul>
        </div>
      </section>
    </>
  );
};
